import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('cms_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('cms_token');
      // Optionally redirect to login
    }
    return Promise.reject(error);
  }
);

// Menu Categories API
export const menuCategoriesAPI = {
  getAll: () => api.get('/menu-categories'),
  getById: (id) => api.get(`/menu-categories/${id}`),
  create: (data) => api.post('/menu-categories', data),
  update: (id, data) => api.put(`/menu-categories/${id}`, data),
  delete: (id) => api.delete(`/menu-categories/${id}`),
};

// Menu Items API
export const menuItemsAPI = {
  getAll: (params) => api.get('/menu-items', { params }),
  getById: (id) => api.get(`/menu-items/${id}`),
  getByCategory: (categorySlug) => api.get(`/menu-items/by-category/${categorySlug}`),
  create: (data) => api.post('/menu-items', data),
  update: (id, data) => api.put(`/menu-items/${id}`, data),
  delete: (id) => api.delete(`/menu-items/${id}`),
};

// Restaurant Info API
export const restaurantInfoAPI = {
  getAll: (publicOnly = true) => api.get('/restaurant-info', { 
    params: { public_only: publicOnly } 
  }),
  getByKey: (key) => api.get(`/restaurant-info/${key}`),
  create: (data) => api.post('/restaurant-info', data),
  update: (key, data) => api.put(`/restaurant-info/${key}`, data),
  delete: (key) => api.delete(`/restaurant-info/${key}`),
};

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
  changePassword: (passwords) => api.put('/auth/change-password', passwords),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  updatePassword: (id, passwordData) => api.put(`/users/${id}/password`, passwordData),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats/overview'),
};

// Helper functions for common operations
export const menuAPI = {
  // Get complete menu with categories and items
  getCompleteMenu: async () => {
    try {
      const [categoriesResponse, itemsResponse] = await Promise.all([
        menuCategoriesAPI.getAll(),
        menuItemsAPI.getAll({ is_available: true })
      ]);

      const categories = categoriesResponse.data.data;
      const items = itemsResponse.data.data;

      // Group items by category
      const menuByCategory = categories.map(category => ({
        ...category,
        items: items.filter(item => item.category_id === category.id)
      }));

      return {
        categories: menuByCategory,
        totalItems: items.length,
        totalCategories: categories.length
      };
    } catch (error) {
      console.error('Error fetching complete menu:', error);
      throw error;
    }
  },

  // Get menu for a specific category
  getCategoryMenu: async (categorySlug) => {
    try {
      const response = await menuItemsAPI.getByCategory(categorySlug);
      return response.data;
    } catch (error) {
      console.error(`Error fetching menu for category ${categorySlug}:`, error);
      throw error;
    }
  },

  // Search menu items
  searchItems: async (query, categoryId = null) => {
    try {
      const params = { is_available: true };
      if (categoryId) {
        params.category_id = categoryId;
      }

      const response = await menuItemsAPI.getAll(params);
      const items = response.data.data;

      // Simple text search
      const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        item.description.toLowerCase().includes(query.toLowerCase())
      );

      return {
        items: filteredItems,
        count: filteredItems.length,
        query
      };
    } catch (error) {
      console.error('Error searching menu items:', error);
      throw error;
    }
  }
};

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteImage: (filename) => api.delete(`/upload/image/${filename}`),
  listImages: () => api.get('/upload/images'),
};

// Default export
export default api;